import { isAcTerminalId } from "../ac";
import { analyzeBatterySourceRanges, type BatteryStack } from "../battery-stack";
import { getDefinition, type DefLookup } from "../catalog";
import { effectiveTerminals } from "../effective";
import type { ComponentDefinition, ComponentNode, Issue, System } from "../types";
import { buildGraph, v, type Graph, type Vertex } from "./graph";

// Manufacturer model boundaries, not a rule for all LiFePO4 batteries.
// Victron Lithium Battery Smart and Lithium NG 25.6 V manuals, §3.2–3.3:
// https://www.victronenergy.com/media/pg/Lithium_Battery_Smart/en/system-design-and-bms-selection-guide.html
// https://www.victronenergy.com/media/pg/Lithium_NG_battery_25,6_V/en/system-design-and-bms-selection-guide.html
// Their cell-monitoring electronics require an external, family-compatible
// BMS. A drop-in battery's internal BMS and an unknown battery are not missing
// an external BMS just because no separate controller appears on the canvas.
const EXTERNAL_BMS_BATTERIES = new Set([
  "batt-victron-lfp-100", "batt-victron-lfp-200", "batt-victron-lfp-330",
  "batt-victron-lfp-ng-200",
]);

type BmsPorts = { positive: string; negative?: string; switchedPositive?: string };
const BMS_PORTS: Record<string, BmsPorts> = {
  "dist-victron-lynx-smart-bms-500": {
    positive: "batt-pos", negative: "batt-neg", switchedPositive: "bus-pos",
  },
  "dist-victron-lynx-smart-bms-1000": {
    positive: "batt-pos", negative: "batt-neg", switchedPositive: "bus-pos",
  },
  // VE.Bus BMS V2 obtains its return through VE.Bus. Its positive supply is
  // enough to identify a candidate bank, never enough to verify operation.
  "bms-victron-vebus-bms-v2": { positive: "pwr-pos" },
  "bms-victron-smallbms-ng": { positive: "pwr-pos", negative: "pwr-neg" },
  "bms-victron-smart-12-200": {
    positive: "batt-pos", negative: "gnd", switchedPositive: "system-pos",
  },
};

type Part = { node: ComponentNode; def: ComponentDefinition };

/** A custom clone can keep the original id while becoming different hardware.
 * Names, labels and layout may change, but manufacturer/model identity and the
 * electrical capabilities supporting a model-specific assertion must agree. */
function isSupportedModel({ node, def }: Part): boolean {
  const original = getDefinition(def.id);
  if (!original || def.category !== original.category || def.manufacturer !== original.manufacturer ||
    def.model !== original.model || def.chemistry !== original.chemistry ||
    def.voltage !== original.voltage || def.capacityAh !== original.capacityAh ||
    def.internalDcConnections?.length || def.oneWayDcConnections?.length ||
    JSON.stringify(def.bms?.switching) !== JSON.stringify(original.bms?.switching) ||
    def.bms?.placement !== original.bms?.placement ||
    JSON.stringify(def.bms?.compatibleBatteryIds) !== JSON.stringify(original.bms?.compatibleBatteryIds)) return false;
  const terminals = effectiveTerminals(def, node);
  return terminals.length === original.terminals.length && original.terminals.every(expected => {
    const matches = terminals.filter(t => t.id === expected.id);
    return matches.length === 1 && matches[0].polarity === expected.polarity &&
      matches[0].direction === expected.direction && matches[0].maxAmp === expected.maxAmp;
  });
}

function parts(system: System, lookup: DefLookup): Part[] {
  return system.nodes.flatMap(node => {
    if (node.kind !== "component") return [];
    const def = lookup(node.definitionId, node.customDefinition);
    return def ? [{ node, def }] : [];
  });
}

/** Only real DC conductors may identify a supply or bypass. All BMS internal
 * paths are cut: the system-side post must not impersonate the battery-side
 * connection, and another controlled BMS is not a proven copper bypass. */
function bmsSupplyGraph(system: System, lookup: DefLookup, components: Part[]): Graph {
  const graph = buildGraph(system, lookup, { excludeSense: true });
  const terminals = new Set(components.flatMap(({ node, def }) =>
    effectiveTerminals(def, node)
      .filter(t => t.polarity !== "signal" && !isAcTerminalId(t.id))
      .map(t => v(node.id, t.id))));
  const external = new Set(components.filter(p => p.def.bms?.placement === "external").map(p => p.node.id));
  return {
    ...graph,
    adj: new Map([...graph.adj].filter(([vertex]) => terminals.has(vertex)).map(([vertex, edges]) => [
      vertex,
      edges.filter(edge => terminals.has(edge.vertex) &&
        !(edge.via.kind === "intra" && external.has(edge.via.link.nodeId))),
    ])),
  };
}

function path(graph: Graph, from: Vertex, targets: ReadonlySet<Vertex>): string[] | undefined {
  const queue = [from];
  const previous = new Map<Vertex, { vertex: Vertex; cableId?: string }>();
  const seen = new Set(queue);
  for (let i = 0; i < queue.length; i++) {
    const current = queue[i];
    if (targets.has(current)) {
      const cableIds: string[] = [];
      let at = current;
      while (previous.has(at)) {
        const step = previous.get(at)!;
        if (step.cableId) cableIds.push(step.cableId);
        at = step.vertex;
      }
      return cableIds.reverse();
    }
    for (const edge of graph.adj.get(current) ?? []) {
      if (seen.has(edge.vertex)) continue;
      seen.add(edge.vertex);
      previous.set(edge.vertex, {
        vertex: current, cableId: edge.via.kind === "cable" ? edge.via.cableId : undefined,
      });
      queue.push(edge.vertex);
    }
  }
}

function portsFor({ node, def }: Part): BmsPorts | undefined {
  const terminals = effectiveTerminals(def, node);
  const declared = BMS_PORTS[def.id];
  if (declared && isSupportedModel({ node, def })) {
    if (!terminals.some(t => t.id === declared.positive && t.polarity === "positive") ||
      (declared.negative && !terminals.some(t => t.id === declared.negative && t.polarity === "negative"))) return;
    return declared;
  }
  // An explicitly described custom controller can be associated with its
  // unambiguous input supply, but has no verified model-specific disconnect.
  // Do not turn missing manufacturer verification into an absent BMS claim.
  const positive = terminals.filter(t => t.polarity === "positive" && t.direction === "input");
  const negative = terminals.filter(t => t.polarity === "negative" && t.direction === "input");
  if (positive.length !== 1 || negative.length > 1) return;
  return { positive: positive[0].id, negative: negative[0]?.id };
}

function suppliedBy(graph: Graph, part: Part, ports: BmsPorts, bank: BatteryStack): string[] | undefined {
  const positive = path(graph, v(part.node.id, ports.positive), new Set(bank.topPositives));
  if (!positive) return;
  const negative = ports.negative
    ? path(graph, v(part.node.id, ports.negative), new Set(bank.bottomNegatives))
    : [];
  return negative ? [...positive, ...negative] : undefined;
}

/** Check requirements and known family compatibility; never certify battery
 * protection from power wiring alone. Current catalog batteries omit the BMS
 * data-chain terminals, and most controllers omit shutdown terminals. Even a
 * Lynx contactor is backup protection, not a replacement for ATC/ATD or DVCC.
 * smallBMS NG installation §3.2 explicitly requires effective load/charger
 * shutdown and compatible signal polarity:
 * https://www.victronenergy.com/media/pg/smallBMS_NG/en/installation.html */
export function runBmsDiagnostics(system: System, lookup: DefLookup): Issue[] {
  const components = parts(system, lookup);
  const controllers = components.filter(({ def }) => def.bms?.placement === "external");
  const supportedBatteries = components.filter(part => part.def.category === "battery" &&
    EXTERNAL_BMS_BATTERIES.has(part.def.id) && isSupportedModel(part));
  if (supportedBatteries.length === 0 && controllers.length === 0) return [];
  const graph = bmsSupplyGraph(system, lookup, components);
  const isWired = (part: Part) => effectiveTerminals(part.def, part.node).some(t =>
    graph.adj.get(v(part.node.id, t.id))?.some(e => e.via.kind === "cable"));
  // Signal wiring is evidence that a controller is being installed, although
  // it can never prove a battery supply or a power-disconnect path.
  const validTerminals = new Set(components.flatMap(({ node, def }) =>
    effectiveTerminals(def, node).map(t => v(node.id, t.id))));
  const validCables = system.cables.filter(c =>
    validTerminals.has(v(c.fromNodeId, c.fromTerminalId)) &&
    validTerminals.has(v(c.toNodeId, c.toTerminalId)));
  // An unwired palette item is not an installed battery yet.
  const required = supportedBatteries.filter(isWired);
  const analysis = analyzeBatterySourceRanges(system, lookup, graph);
  const issues: Issue[] = [];
  const unverifiedNodes = new Set<string>();
  const unverifiedControllers = new Set<string>();
  const unverifiedCables = new Set<string>();
  const candidates = controllers.flatMap(part => {
    const ports = portsFor(part);
    if (!ports) return [];
    const supplies = analysis.ranges.flatMap(bank => {
      const cables = suppliedBy(graph, part, ports, bank);
      return cables ? [{ bank, cables }] : [];
    });
    return supplies.length ? [{ part, ports, supplies }] : [];
  });
  // An unsupported port map, incomplete supply or signal-only installation
  // still needs review. Failure to prove a supply must not make an installed
  // controller disappear, nor establish that a particular battery is protected.
  for (const part of controllers) {
    if (candidates.some(candidate => candidate.part.node.id === part.node.id)) continue;
    const attached = validCables.filter(c => c.fromNodeId === part.node.id || c.toNodeId === part.node.id);
    if (attached.length === 0) continue;
    unverifiedNodes.add(part.node.id);
    unverifiedControllers.add(part.node.id);
    attached.forEach(c => unverifiedCables.add(c.id));
  }

  if (analysis.complete) {
    for (const battery of required) {
      const connected = candidates.filter(c => c.supplies.some(s => s.bank.batteryNodeIds.includes(battery.node.id)));
      if (connected.length === 0) {
        issues.push({
          code: "battery-bms-missing", severity: "critical",
          title: { key: "diag.battery-bms-missing.title", vars: { label: battery.node.label ?? battery.def.name } },
          message: { key: "diag.battery-bms-missing.message", vars: { label: battery.node.label ?? battery.def.name } },
          fix: { key: "diag.battery-bms-missing.fix" }, nodeIds: [battery.node.id],
        });
      }
      // A second, compatible BMS does not erase a known wrong-family unit
      // connected to this bank. Report each physical mismatch independently.
      for (const candidate of connected) {
        const ids = candidate.part.def.bms?.compatibleBatteryIds;
        if (!ids || ids.includes(battery.def.id)) continue;
        issues.push({
          code: "battery-bms-incompatible", severity: "critical",
          title: { key: "diag.battery-bms-incompatible.title" },
          message: { key: "diag.battery-bms-incompatible.message", vars: {
            label: battery.node.label ?? battery.def.name,
            bms: candidate.part.node.label ?? candidate.part.def.name,
          } },
          fix: { key: "diag.battery-bms-incompatible.fix" },
          nodeIds: [battery.node.id, candidate.part.node.id],
          cableIds: [...new Set(candidate.supplies.filter(s => s.bank.batteryNodeIds.includes(battery.node.id)).flatMap(s => s.cables))],
        });
      }
    }
  } else {
    // Extraction limits are not evidence that a BMS is absent.
    required.forEach(p => unverifiedNodes.add(p.node.id));
    controllers.forEach(p => { unverifiedNodes.add(p.node.id); unverifiedControllers.add(p.node.id); });
  }

  for (const candidate of candidates) {
    const { part, ports, supplies } = candidate;
    unverifiedNodes.add(part.node.id);
    unverifiedControllers.add(part.node.id);
    for (const supply of supplies) {
      supply.bank.batteryNodeIds.forEach(id => unverifiedNodes.add(id));
      supply.cables.forEach(id => unverifiedCables.add(id));
    }
    if (!ports.switchedPositive || !part.def.terminals.some(t => t.id === ports.switchedPositive && t.polarity === "positive")) continue;
    const bypass = path(graph, v(part.node.id, ports.positive), new Set([v(part.node.id, ports.switchedPositive)]));
    if (!bypass) continue;
    issues.push({
      code: "bms-disconnect-bypassed", severity: "critical",
      title: { key: "diag.bms-disconnect-bypassed.title" },
      message: { key: "diag.bms-disconnect-bypassed.message", vars: { label: part.node.label ?? part.def.name } },
      fix: { key: "diag.bms-disconnect-bypassed.fix" }, nodeIds: [part.node.id], cableIds: bypass,
    });
  }
  if (unverifiedNodes.size > 0) issues.push({
    code: "bms-control-unverified", severity: "warning",
    title: { key: "diag.bms-control-unverified.title" },
    message: { key: "diag.bms-control-unverified.message", vars: { count: unverifiedControllers.size || required.length } },
    fix: { key: "diag.bms-control-unverified.fix" },
    nodeIds: [...unverifiedNodes].sort(), cableIds: [...unverifiedCables].sort(),
  });
  return issues;
}
